import { useSelector, useDispatch } from "react-redux";
import { useState, useEffect } from "react";
import { SearchTodo } from "../components/searchTodo/SearchTodo";
import { getFilterState } from "../functions/filterState/filterState";
import { setFilteredSearchResults } from "../redux/actions/setSearchResultAction";
import { filterOverview, filterSearchTodoList } from "../utils/todo-utils";
import { useIsMobile } from "../functions/deviceIdentity/getDeviceType";
import { getLatestTaskList } from "../redux/actions/Firebase/getLatestTaskListAction";
import { getTimelineTodoList } from "../store/selector";

const Search = () => {
    const searchResults = useSelector((state) => state.searchResult);
    const projects = useSelector((state) => state.taskList.data);
    const zoomLevel = useSelector((state) => state.userSettings.zoomLevel);
    const localSearchState = useState({tasks: [], projects: [], Text: ''});
    const dispatch = useDispatch();
    const isMobile = useIsMobile();
    const [searchText, setSearchText] = useState("");
    const [taskAccordionState, setTaskAccordionState] = useState(false);
    const [projectAccordionState, setProjectAccordionState] = useState(true);
    const feature = "Overview";
    const filterStateName = getFilterState(feature) ?? "overviewFilterState";
    const filterState = useSelector(state => state[filterStateName]);
    const [filteredTodos, setFilteredTodos] = useState({ projects: [], tasks: [] });
    const [allTodos, setAllTodos] = useState([]);
    const handleSearch = () => {
        const trimmedSearchText = searchText?.trim();
        if (trimmedSearchText) {
            const now = new Date();
            const searchState = { ...filterState, Text: trimmedSearchText };

            // Filter tasks only when there are tasks to process
            if (allTodos.length > 0) {
                const filteredTasks = getTimelineTodoList(allTodos);
                const filteredResults = filterSearchTodoList(filteredTasks, searchState, now);

                // Dispatch results only if they differ from current search results
                if (
                    JSON.stringify(filteredResults.tasks) !== JSON.stringify(searchResults.tasks) ||
                    JSON.stringify(filteredResults.projects) !== JSON.stringify(searchResults.projects)
                ) {
                    dispatch(setFilteredSearchResults({ ...filteredResults, Text: trimmedSearchText }));
                }
            }
        }
    };
    const handleSearchTextChange = (e) => {
       
        setSearchText(e.target.value);
    };
    const handleEnter = (e) => {
        if (e.key === 'Enter') {
            handleSearch();
        }
    }
    useEffect(() => {
        setTaskAccordionState(Array.isArray(searchResults.tasks) && searchResults.tasks.length > 0);
    }, [searchResults.tasks]);

    
    useEffect(() => {
        setProjectAccordionState(Array.isArray(searchResults.projects) && searchResults.projects.length > 0);
    }, [searchResults.projects]);
    useEffect(() => {
        if (projects?.length) {
            const newFilteredTodos = filterOverview(projects, filterState, new Date());
            setFilteredTodos(newFilteredTodos);
        }
    }, [projects, filterState]);
    useEffect(() => {
        const newAllTodos = [...filteredTodos.projects, ...filteredTodos.tasks];
        setAllTodos(newAllTodos);
    }, [filteredTodos]);
    useEffect(() => {
        // Fetch projects only if they are not already loaded
        if (!projects?.length) {
            dispatch(getLatestTaskList());
            return; // Exit early after fetching the task list
        }

        // Perform search filtering only if search text exists and has changed
        const trimmedSearchText = searchResults.Text?.trim();
        if (trimmedSearchText) {
            const now = new Date();
            const searchState = { ...filterState, Text: trimmedSearchText };

            // Filter tasks only when there are tasks to process
            if (allTodos.length > 0) {
                const filteredTasks = getTimelineTodoList(allTodos);
                const filteredResults = filterSearchTodoList(filteredTasks, searchState, now);

                // Dispatch results only if they differ from current search results
                if (
                    JSON.stringify(filteredResults.tasks) !== JSON.stringify(searchResults.tasks) ||
                    JSON.stringify(filteredResults.projects) !== JSON.stringify(searchResults.projects)
                ) {
                    dispatch(setFilteredSearchResults({ ...filteredResults, Text: trimmedSearchText }));
                }
            }
        }
    }, [projects, filterState, searchResults.Text]);

   
    return (
        <>
            <div className="gt-content__todo">
                {isMobile && (
                    <div style={{ display: "flex" }}>
                        <input
                            type="text"
                            id="new-section-title"
                            placeholder="Search"
                            className="gw-input"
                            value={searchText}  
                            onChange={handleSearchTextChange}
                            onKeyDown={ handleEnter}
                            style={{ margin: "1em" }}
                        />
                        <button
                            className="gw-button gw-button--s1 gw-button--primary"
                            style={{ margin: "1.2em 0.5em 1em 0.5em" }}
                            onClick={handleSearch}  // Execute search on click
                        >
                            Search
                        </button>
                    </div>
                )}

                <div className={`gt-accordion ${taskAccordionState ? 'gt-accordion--active' : ''}`}
                    aria-expanded={taskAccordionState} style={{ fontSize: isMobile ? '10px' : `${zoomLevel}px` }}>
                    <div className="gt-accordion__activator">
                        <svg xmlns="http://www.w3.org/2000/svg">
                            <use href="#globaltasks-svg-todo-next"></use>
                        </svg>
                        ({searchResults.tasks?.length || 0}) Tasks
                        <button className="gw-icon-button gw-icon-button--icon" onClick={() => { setTaskAccordionState(prevState => !prevState) }}>
                            <i className="gw-icon-button__icon gw-button__icon--chevron-down"></i>
                        </button>
                    </div>
                    <div className="gt-accordion__content">
                        {searchResults.tasks?.length > 0 && searchResults.tasks.map((task) => (
                            <SearchTodo key={task.id} todo={task} projects={projects} zoomLevel={zoomLevel} />
                        ))}
                    </div>
                </div>

                <div className={`gt-accordion ${projectAccordionState ? 'gt-accordion--active' : ''}`}
                    aria-expanded={projectAccordionState} style={{ fontSize: isMobile ? '10px' : `${zoomLevel}px` }}>
                    <div className="gt-accordion__activator">
                        <svg xmlns="http://www.w3.org/2000/svg">
                            <use href="#globaltasks-svg-todo-everything-else"></use>
                        </svg>
                        ({searchResults.projects?.length || 0}) Sections
                        <button className="gw-icon-button gw-icon-button--icon" onClick={() => { setProjectAccordionState(prevState => !prevState) }}>
                            <i className="gw-icon-button__icon gw-button__icon--chevron-down"></i>
                        </button>
                    </div>
                    <div className="gt-accordion__content">
                        {searchResults.projects?.length > 0 && searchResults.projects.map((project) => (
                            <SearchTodo key={project.id} todo={project} projects={projects} zoomLevel={zoomLevel} />
                        ))}
                    </div>
                </div>

            </div>
        </>
    );
};

export default Search;